/**
 * The languages this app speaks, and how a request's language is decided.
 *
 * Shared by the browser and the Worker on purpose. The web app uses this list
 * to negotiate i18next resources and `Intl` locales; the Worker uses the same
 * list to decide which language a brand-new company's starter data is written
 * in. Adding a language in one place without the other is how a company ends
 * up with a translated UI listing rooms named in a language nobody picked.
 */

export const SUPPORTED_LANGUAGES = ['en', 'es'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * What everything falls back to. English, because it is the language the seed
 * data was originally authored in and the one every other translation is
 * checked against.
 */
export const DEFAULT_LANGUAGE = 'en' as const satisfies SupportedLanguage;

function isSupported(value: string): value is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/**
 * The `Intl` locale to format dates, times and money in for a given language.
 *
 * Separate from the language itself because `Intl` wants a region to decide
 * day/month order and the 12- or 24-hour clock, and the language alone does not
 * carry one. Spanish gets `es-ES`, whose day-month-year order and 24-hour clock
 * are what a Spanish reader expects in either hemisphere; the alternative,
 * bare `es`, is resolved differently by different ICU builds, and a date that
 * silently changes shape between the Worker and a headless PDF render is worse
 * than one that is explicit.
 */
const INTL_LOCALES: Readonly<Record<SupportedLanguage, string>> = {
  en: 'en-US',
  es: 'es-ES',
};

export function localeFor(language: SupportedLanguage): string {
  return INTL_LOCALES[language];
}

/**
 * The word between a date and a time — "at 9:00 AM", "a las 9:00".
 *
 * `Intl` will not produce this: `dateStyle`/`timeStyle` together give a locale's
 * own joined form, but the two halves here are formatted separately because the
 * text message needs a short date with a full time, a combination no single
 * style produces. Kept beside the locales so a new language cannot add one
 * without the other. GSM-7-safe in every language, since the SMS reminder
 * interpolates it.
 */
const AT_TIME: Readonly<Record<SupportedLanguage, string>> = {
  en: 'at',
  es: 'a las',
};

export function atTimeWord(language: SupportedLanguage): string {
  return AT_TIME[language];
}

/**
 * Reduces a BCP-47 tag to a language this app has translations for, or null.
 *
 * Only the primary subtag is inspected, so 'es-MX', 'es-419' and 'ES' all
 * resolve to 'es' — matching i18next's `nonExplicitSupportedLngs` behavior,
 * which is what decides the UI language for the same visitor.
 */
export function normalizeLanguage(tag: string | null | undefined): SupportedLanguage | null {
  if (!tag) return null;
  const primary = tag.trim().split(/[-_]/)[0]?.toLowerCase();
  return primary && isSupported(primary) ? primary : null;
}

/**
 * The best supported language named by an `Accept-Language` header.
 *
 * Entries are ranked by q-value, ties broken by the order they were sent —
 * the header's own preference order. `q=0` means "explicitly not this one",
 * so those are dropped rather than ranked last. A header naming only
 * unsupported languages returns null, leaving the choice to the caller's own
 * fallback rather than guessing.
 */
export function languageFromAcceptLanguage(header: string | null | undefined): SupportedLanguage | null {
  if (!header) return null;

  const ranked = header
    .split(',')
    .map((part, index) => {
      const [tag, ...parameters] = part.split(';').map((piece) => piece.trim());
      const quality = parameters.find((parameter) => parameter.startsWith('q='));
      const parsed = quality ? Number.parseFloat(quality.slice(2)) : 1;
      return { tag: tag ?? '', quality: Number.isFinite(parsed) ? parsed : 0, index };
    })
    .filter((entry) => entry.tag !== '' && entry.quality > 0)
    .sort((a, b) => b.quality - a.quality || a.index - b.index);

  for (const entry of ranked) {
    const language = normalizeLanguage(entry.tag);
    if (language) return language;
  }
  return null;
}

/**
 * The language to write a new company's starter data in.
 *
 * The explicit tag wins: it is the language the person is *looking at* as they
 * sign up, sent by the app itself, which is a stronger signal than the browser
 * preference list the header carries. The header is the fallback for callers
 * that send no tag of their own — the marketing site's trial-signup API, and
 * anything scripted. English when neither says anything usable.
 */
export function resolveLanguage(
  explicitTag: string | null | undefined,
  acceptLanguageHeader?: string | null | undefined,
): SupportedLanguage {
  return normalizeLanguage(explicitTag) ?? languageFromAcceptLanguage(acceptLanguageHeader) ?? DEFAULT_LANGUAGE;
}
